'use client';

import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ActionIcon, getActionColor } from '../utils/action-style';
import { useLogFormatters } from '../utils/use-log-formatters';
import type { AuditLog } from '../types';

interface SystemLogCompactRowProps {
  log: AuditLog;
}

export function SystemLogCompactRow({ log }: SystemLogCompactRowProps) {
  const t = useTranslations('systemLogs');
  const { translateAction, translateSubjectType } = useLogFormatters();

  const date = new Date(log.created_at);
  const formattedDate = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });

  const actionLabel = translateAction(log.action);
  const subjectLabel = log.subject_type
    ? translateSubjectType(log.subject_type)
    : null;

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: action badge + subject info */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <Badge
            variant='secondary'
            className={cn(
              'shrink-0 gap-1 text-xs font-medium',
              getActionColor(log.action)
            )}
          >
            <ActionIcon action={log.action} className='h-3 w-3' />
            {actionLabel}
          </Badge>
          {subjectLabel && (
            <span className='text-muted-foreground truncate text-sm'>
              {log.subject_name
                ? `${log.subject_name} (${subjectLabel})`
                : `${subjectLabel}${log.subject_id ? ` #${log.subject_id}` : ''}`}
            </span>
          )}
        </div>
      </div>

      {/* Center: user name (hidden on mobile) */}
      <span className='text-muted-foreground hidden shrink-0 text-sm md:inline'>
        {log.user ? log.user.name : t('systemAction')}
      </span>

      {/* Right: timestamp */}
      <span className='text-muted-foreground shrink-0 text-xs whitespace-nowrap'>
        {formattedDate} {formattedTime}
      </span>
    </div>
  );
}
